import type { SiteId } from "~/hec/utils/site-id";

/**
 * TrustArc consent-banner config (COM-184).
 *
 * TrustArc's notice script lives on `consent.trustarc.com` for every site —
 * the banner text/config bundle it fetches after executing comes from the
 * SAME host, so one preconnect covers the whole chain (see root.tsx `links`).
 */
export const TRUSTARC_HOST = "https://consent.trustarc.com";

/**
 * Per-site TrustArc `domain` registration. These are the values the Builder
 * `TrustArcBanner` block passes in live Builder content (verified against the
 * rendered content payloads: FSA → `fsastore.com`, HSA → `hsastore.com`).
 * They are the TrustArc-console registration keys, NOT the request hostname —
 * dev/staging tiers still use the production-registered domain, exactly like
 * the Builder block does today.
 *
 * `null` disables the SSR bootstrap for that site. WDH carries no
 * TrustArcBanner block in its Builder content (verified on the dev tier
 * 2026-08-25) — consistent with WDH's exclusion from the other vendor configs
 * (Segment, Annex). If WDH ever adds the block in Builder, its client-side
 * injection path still works without any change here; add the domain here to
 * give it the same SSR acceleration.
 */
export const TRUSTARC_DOMAINS: Record<SiteId, string | null> = {
  FSASTORE: "fsastore.com",
  HSASTORE: "hsastore.com",
  WDH: null,
};

/**
 * The exact notice-script URL the Builder `TrustArcBanner` block builds
 * (`app/hec/builder/blocks/components/trustarc-banner/index.tsx`, ported
 * from hec-composable) — kept byte-identical so the SSR bootstrap and the
 * block's client fallback dedupe onto the same script (`id="trustarc"`
 * guard) and TrustArc sees the same integration flags (`gtm=1` etc.).
 */
export const trustArcNoticeUrl = (domain: string): string =>
  `${TRUSTARC_HOST}/notice?domain=${domain}&c=teconsent&js=nj&noticeType=bb&text=true&gtm=1&pcookie`;
